import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BackHeader from '../../components/BackHeader';
import NavigationBar from '../../components/NavigationBar';
import ScreenshotReceivedPopup from '../../components/ScreenshotReceivedPopup';
import { showChannelTalk, trackChannelTalkEvent } from '../../services/ChannelService';
import { getCurrentLanguage } from '../../config/languageConfig';
import { pollingService } from '../../services/PollingService';
import useTranslation from '../../hooks/useTranslation';
import axios from 'axios';

/**
 * 로컬스토리지 키 상수
 */
const SERVICE_USER_ID_KEY = 'serviceUserId';

/**
 * 로컬스토리지에서 serviceUserId 조회
 */
const getServiceUserId = (): string | null => {
  try {
    return localStorage.getItem(SERVICE_USER_ID_KEY);
  } catch (error) {
    console.warn('[AddressScreenshotUpload] Failed to get serviceUserId from localStorage:', error);
    return null;
  }
};

/**
 * 이미지 업로드 API 호출 (운영 서버만)
 */
const uploadImage = async (file: File): Promise<void> => {
  const formData = new FormData();
  formData.append('image', file);
  
  const serviceUserId = getServiceUserId();
  
  // 운영 서버 URL
  const PRODUCTION_API_URL = 'https://api.brainbackdoor.com';
  
  // API 요청 URL 구성
  let apiUrl = `${PRODUCTION_API_URL}/api/delivery-addresses/image-upload`;
  if (serviceUserId) {
    apiUrl += `?serviceUserId=${serviceUserId}`;
  }
  
  const response = await axios.post(apiUrl, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 30000, // 30초 타임아웃 (이미지 업로드는 시간이 걸릴 수 있음)
  });
  
  return response.data;
};

const AddressScreenshotUpload: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const { t } = useTranslation();

  // 파일 선택 핸들러
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    console.log('Selected file:', file.name, file.type, file.size);
    
    setIsUploading(true);
    
    try {
      await uploadImage(file);
      console.log('Image upload successful');
      
      // 팝업 표시
      setShowSuccessPopup(true);
      
      // 백그라운드 polling 시작
      const serviceUserId = getServiceUserId();
      if (serviceUserId) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[AddressScreenshotUpload] Starting background polling for serviceUserId:', serviceUserId);
        }
        
        // polling 시작 (성공 콜백은 BackgroundPollingManager에서 처리)
        pollingService.startPolling(serviceUserId);
      } else {
        console.warn('[AddressScreenshotUpload] No serviceUserId available for polling');
      }
      
    } catch (error) {
      console.error('Image upload failed:', error);
      // TODO: 에러 처리는 나중에 구현
    } finally {
      setIsUploading(false);
      // 파일 input 초기화
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Auto-Set via Pic 버튼 클릭 (파일 선택 다이얼로그 열기)
  const handleAutoSetViaPic = () => {
    if (isUploading) return; // 업로드 중이면 무시
    fileInputRef.current?.click();
  };

  // 성공 팝업 닫기
  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    // 팝업을 닫아도 백그라운드 polling은 계속 진행됨
  };

  // Back to Preference 버튼 클릭
  const handleBackToPreference = () => {
    navigate('/address/method');
  };

  // 채널톡 도움말 열기
  const handleOpenHelp = () => {
    trackChannelTalkEvent('address_screenshot_help_requested', {
      page: 'screenshot_upload'
    });
    
    if (window.ChannelIO) {
      try {
        window.ChannelIO('boot', {
          "pluginKey": "4de468b6-e6bf-4446-a6d0-fb3ccb1132a6",
          "language": getCurrentLanguage(),
          "hideDefaultLauncher": true,
          "customLauncherSelector": "#custom-channel-button" 
        });
        
        setTimeout(() => {
          showChannelTalk();
        }, 500);
      } catch (error) {
        console.error('Failed to initialize ChannelTalk on help request:', error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header with back button */}
      <BackHeader title={t('address.screenshot.title')} />
      
      {/* 숨겨진 파일 입력 요소 */}
      <input
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileSelect}
      />
      
      <div className="flex-1 px-4 py-6 pb-48">
        {/* Upload Area */}
        <div className="mb-8">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 py-6 text-center bg-white">
            {/* Upload Icon */}
            <div className="mb-4">
              <svg 
                className="mx-auto h-12 w-12 text-gray-400" 
                stroke="currentColor" 
                fill="none" 
                viewBox="0 0 48 48"
              >
                <path 
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" 
                  strokeWidth={2} 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                />
              </svg>
            </div>
            
            {/* Instructions */}
            <div className="space-y-2">
              <div className="flex items-center justify-center text-sm text-gray-700">
                <span className="text-green-600 mr-2">✓</span>
                {t('address.screenshot.instruction.screenshot')}
              </div>
              <div className="flex items-center justify-center text-sm text-gray-700">
                <span className="text-green-600 mr-2">✓</span>
                {t('address.screenshot.instruction.fullAddress')}
              </div>
              <div className="flex items-center justify-center text-sm text-gray-700">
                <span className="text-green-600 mr-2">✓</span>
                <span className="font-semibold">{t('address.screenshot.instruction.hostMessage')}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* 버튼 영역 */}
        <div className="mt-6">
          <div className="flex flex-col gap-3">
            {/* Auto-Set via Pic Button */}
            <button
              type="button"
              onClick={handleAutoSetViaPic}
              disabled={isUploading}
              className={`w-full py-3 rounded-lg font-semibold text-white transition-all duration-150 shadow-sm ${
                isUploading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-red-500 hover:bg-red-600 active:bg-red-700'
              }`}
            >
              {isUploading ? (
                <div className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{t('address.screenshot.uploading')}</span>
                </div>
              ) : (
                t('address.screenshot.uploadButton')
              )}
            </button>
            
            {/* Back to Preference Button */}
            <button
              type="button"
              onClick={handleBackToPreference}
              className="w-full py-3 rounded-lg font-semibold text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 active:bg-gray-100 transition-all duration-150 shadow-sm"
            >
              {t('address.backToPreference')}
            </button>
          </div>
        </div>
        
        {/* Help link */}
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={handleOpenHelp}
            className="text-gray-600 hover:text-gray-800 focus:outline-none"
          >
            {t('address.help')}
          </button>
        </div>
      </div>
      
      {/* Navigation Bar */}
      <NavigationBar />
      
      {/* Success Popup */}
      <ScreenshotReceivedPopup
        isVisible={showSuccessPopup}
        onClose={handleCloseSuccessPopup}
      />
    </div>
  );
};

export default AddressScreenshotUpload; 